import React from 'react';
import axios from 'src/axios';
import { AppContext } from 'src/store/ContextStore';
import { APICONSTANTS } from 'src/components/Constants';
import {
  CONSTANTS,
  linkParametersMap
} from 'src/components/questionnaire/Constants';

// Resolves the specialty options and labels for the Generate/Send screen.
// When the per-specialty config feature is on, options come from the Specialty
// collection (so admin-created specialties like Reproductive are selectable);
// when off, the legacy fixed list (CONSTANTS.linkParameters + linkParametersMap)
// is used, byte-for-byte. The label map always merges the legacy labels so
// historical link_parameters still render a friendly name.
export default function useSpecialties() {
  // Read the context defensively: AppContext defaults to null, and this hook is
  // now also used from the pedigree sidebar, so a mount outside the admin
  // provider (tests, isolated renders) must fall back to the legacy option list
  // rather than throw on destructuring null.
  const { content } = React.useContext(AppContext) || {};
  const enabled = Boolean(
    content?.settings?.systemFeatures?.FEATURE_FHQ_SPECIALITY_CONFIG
  );
  const [specialties, setSpecialties] = React.useState([]);

  React.useEffect(() => {
    if (!enabled) return;
    // Include archived so labels/colours still resolve for historical links; the
    // picker options below are limited to active specialties.
    axios
      .get(`${APICONSTANTS.specialties}?includeArchived=true`)
      .then((res) => setSpecialties(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [enabled]);

  return React.useMemo(() => {
    // Labels and colours cover ALL specialties (including archived) so a link
    // tagged with an archived specialty still renders its name and colour.
    const labelMap = { ...linkParametersMap };
    const colourMap = {};
    specialties.forEach((s) => {
      labelMap[s.key] = s.name;
      colourMap[s.key] = s.colour || '#2ba9bf';
    });
    // Only active specialties can be selected for new (or edited) links.
    const active = specialties.filter((s) => !s.archived);
    const optionKeys =
      enabled && active.length
        ? active.map((s) => s.key)
        : CONSTANTS.linkParameters;
    return { enabled, specialties, optionKeys, labelMap, colourMap };
  }, [enabled, specialties]);
}
